import { noop } from 'lodash';

import { DocumentDetailsPanel } from '~/components/Shared/DocumentDetailsPanel/DocumentDetailsPanel';
import { useDocumentMultipleLocationsModal } from '~/components/Shared/EcModal/useDocumentMultipleLocationsModal';
import { SearchTextMatch } from '~/types/Search';
import { openInNewTab } from '~/utils/browser';

import { useClientSubDomain } from './useClientSubDomain';

type DocumentHanlder = {
  id: number;
  name: string;
  file_type: string;
};

// Shape expected by MultipleLocationsModal
type ModalHandler = {
  id: number;
  document_name: string;
  file_type: string;
  path: Array<{ id: number; name: string }>;
  visibility_level: Array<string | boolean>;
};

type Props = {
  index: number;
  count: number;
  handlers?: DocumentHanlder[];
  featuredClauses: string[];
  fullTextMatches?: SearchTextMatch;
  onHide: () => void;
  onNavigate: (index: number) => void;
  onNavigateToDocument?: (handlerId: number) => void;
};

// value_structured items from evisearch use `name`/`folder_id` instead of `document_name`/`path`.
const toModalHandler = (h: any): ModalHandler => ({
  id: typeof h.id === 'string' ? parseInt(h.id, 10) : h.id,
  document_name: h.document_name ?? h.name ?? '',
  file_type: h.file_type ?? '',
  path: h.path ?? [],
  visibility_level: h.visibility_level ?? [],
});

const EMPTY_RETURN = {
  DetailsPanel: null,
  panelProps: {
    onHide: noop,
  },
};

export const useDocumentDetails = ({
  index,
  count,
  handlers = [],
  featuredClauses,
  fullTextMatches,
  onHide,
  onNavigate,
  onNavigateToDocument,
}: Props) => {
  const client = useClientSubDomain();

  const [
    setDocumentSelected,
    renderMultipleLocationModal,
    setOpenDocumentInNewTab,
  ] = useDocumentMultipleLocationsModal(
    (doc: any) => doc,
    false,
    onNavigateToDocument,
  ) as [
    (doc: ModalHandler[] | null) => void,
    () => React.ReactNode,
    (value: boolean | null) => void,
  ];

  const openMultipleLocationsModal = (
    rawHandlers: any[],
    openInNewTab: boolean,
  ) => {
    setDocumentSelected(rawHandlers.map(toModalHandler));
    setOpenDocumentInNewTab(openInNewTab);
  };

  const handler = (handlers || [])[0];
  if (!handler)
    return {
      ...EMPTY_RETURN,
      renderMultipleLocationModal,
      openMultipleLocationsModal,
    };

  const isLastDocument = index === count - 1;
  const isFirstDocument = index === 0;

  const onOpenDocumentClick = () => {
    if (handlers.length > 1) {
      openMultipleLocationsModal(handlers, true);
    } else {
      openInNewTab(`/${client}/document/${handler.id}`);
    }
  };

  const searchKeyworkMatches = fullTextMatches?.length
    ? fullTextMatches
    : undefined;

  const panelProps: React.ComponentProps<typeof DocumentDetailsPanel> = {
    docHandlerId: handler.id,
    searchKeyworkMatches: searchKeyworkMatches,
    onHide,
    featuredClauses,
    actionsConfig: {
      navigate: {
        onClick: onOpenDocumentClick,
      },
      next: {
        onClick: () => onNavigate(index + 1),
        disabled: isLastDocument,
      },
      previous: {
        onClick: () => onNavigate(index - 1),
        disabled: isFirstDocument,
      },
    },
  };

  return {
    DetailsPanel: DocumentDetailsPanel,
    panelProps,
    renderMultipleLocationModal,
    openMultipleLocationsModal,
  };
};
